import React, { useState, useRef, useEffect } from "react";
import { FiPlus, FiLoader, FiAlertCircle, FiX } from "react-icons/fi";
import { useIntegrationConnection } from "../../hooks/useIntegrationConnection";
import {
  INTEGRATION_REGISTRY,
  COMING_SOON_IDS,
  HIDDEN_IDS,
  ICON_INVERT_IDS,
  IntegrationConfig,
} from "../../config/integrations";
import IntegrationLogo from "../common/IntegrationLogo";

// Type-safe icon wrappers
const PlusIcon = FiPlus as React.FC<React.SVGProps<SVGSVGElement>>;
const LoaderIcon = FiLoader as React.FC<React.SVGProps<SVGSVGElement>>;
const AlertIcon = FiAlertCircle as React.FC<React.SVGProps<SVGSVGElement>>;
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;

interface InlineIntegrationConnectorProps {
  /** Already connected provider IDs to exclude from the list */
  connectedProviders?: string[];
  /** Optional: only show specific integrations */
  availableProviders?: string[];
  /** Callback after successful connection */
  onConnectionSuccess?: (provider: string) => void;
  /** Layout variant */
  variant?: "button" | "dropdown" | "compact";
  /** Custom button text */
  buttonText?: string;
  /** Custom class name */
  className?: string;
}

/**
 * Inline integration connector - allows connecting integrations without
 * navigating to the Integrations settings page.
 *
 * Variants:
 * - "button": A + button that opens a dropdown (default)
 * - "dropdown": Dropdown select
 * - "compact": Minimal button for tight spaces
 */
const InlineIntegrationConnector: React.FC<InlineIntegrationConnectorProps> = ({
  connectedProviders = [],
  availableProviders,
  onConnectionSuccess,
  variant = "button",
  buttonText = "Connect",
  className = "",
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    connect,
    connectingProvider,
    isConnecting,
    connectionError,
    clearError,
    renderModals,
  } = useIntegrationConnection({
    onConnectionSuccess: (provider) => {
      setIsDropdownOpen(false);
      onConnectionSuccess?.(provider);
    },
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Filter available integrations
  const availableIntegrations = INTEGRATION_REGISTRY.filter((integration) => {
    // Exclude hidden and coming soon
    if (HIDDEN_IDS.has(integration.id)) return false;
    if (COMING_SOON_IDS.has(integration.id)) return false;
    // Must be connectable
    if (!integration.connectable) return false;
    // Exclude already connected
    if (connectedProviders.includes(integration.id)) return false;
    // If specific providers specified, filter to those
    if (availableProviders && !availableProviders.includes(integration.id)) return false;
    return true;
  });

  // Group integrations by category for better organization
  const groupedIntegrations = availableIntegrations.reduce((acc, integration) => {
    const category = integration.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(integration);
    return acc;
  }, {} as Record<string, IntegrationConfig[]>);

  const categoryLabels: Record<string, string> = {
    development: "Development",
    "project-management": "Project Management",
    communication: "Communication",
    cloud: "Cloud & DevOps",
    analytics: "Analytics & Finance",
    crm: "CRM & Sales",
  };

  const handleConnect = (provider: string) => {
    clearError();
    connect(provider);
  };

  // Render the trigger button
  const renderTrigger = () => {
    if (variant === "compact") {
      return (
        <button
          type="button"
          className={`su-inline-connector-trigger-compact ${className}`}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          disabled={isConnecting}
          title="Connect new integration"
        >
          {isConnecting ? (
            <LoaderIcon className="su-spinner" />
          ) : (
            <PlusIcon />
          )}
        </button>
      );
    }

    return (
      <button
        type="button"
        className={`su-inline-connector-trigger ${className}`}
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        disabled={isConnecting}
      >
        {isConnecting ? (
          <>
            <LoaderIcon className="su-spinner" />
            <span>Connecting...</span>
          </>
        ) : (
          <>
            <PlusIcon />
            <span>{buttonText}</span>
          </>
        )}
      </button>
    );
  };

  // Render dropdown content
  const renderDropdown = () => {
    if (!isDropdownOpen) return null;

    return (
      <div className="su-inline-connector-dropdown">
        <div className="su-inline-connector-dropdown-header">
          <span>Connect Integration</span>
          <button
            type="button"
            className="su-inline-connector-dropdown-close"
            onClick={() => setIsDropdownOpen(false)}
          >
            <CloseIcon />
          </button>
        </div>

        {connectionError && (
          <div className="su-inline-connector-error">
            <AlertIcon />
            <span>{connectionError}</span>
            <button type="button" onClick={clearError}>
              <CloseIcon />
            </button>
          </div>
        )}

        <div className="su-inline-connector-list">
          {Object.entries(groupedIntegrations).map(([category, integrations]) => (
            <div key={category} className="su-inline-connector-group">
              <div className="su-inline-connector-group-label">
                {categoryLabels[category] || category}
              </div>
              {integrations.map((integration) => (
                <button
                  key={integration.id}
                  type="button"
                  className={`su-inline-connector-item ${
                    connectingProvider === integration.id ? "connecting" : ""
                  }`}
                  onClick={() => handleConnect(integration.id)}
                  disabled={isConnecting}
                >
                  <div className="su-inline-connector-item-icon">
                    <IntegrationLogo
                      domain={integration.domain}
                      name={integration.name}
                      fallbackImage={integration.icon}
                      className={`w-5 h-5 ${
                        ICON_INVERT_IDS.has(integration.id) ? "su-icon-invert" : ""
                      }`}
                    />
                  </div>
                  <span className="su-inline-connector-item-name">
                    {integration.name}
                  </span>
                  {connectingProvider === integration.id && (
                    <LoaderIcon className="su-spinner su-inline-connector-item-loader" />
                  )}
                </button>
              ))}
            </div>
          ))}

          {availableIntegrations.length === 0 && (
            <div className="su-inline-connector-empty">
              All available integrations are connected
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="su-inline-connector" ref={dropdownRef}>
        {renderTrigger()}
        {renderDropdown()}
      </div>

      {/* Pre-connection modals (GitHub warning, API key info, etc.) */}
      {renderModals()}
    </>
  );
};

export default InlineIntegrationConnector;
